import React, { useState } from "react";
import "./RenewAdmission.css";
import { apiService } from "./services/apiService"; // Integrated centralized API service layer

function RenewAdmission() {
  const [searchValue, setSearchValue] = useState("");
  const [admissions, setAdmissions] = useState([]);
  const [renewData, setRenewData] = useState({});
  const [searched, setSearched] = useState(false);

  const searchAdmission = async () => {
    if (!searchValue.trim()) return;
    
    try {
      // Using centralized abstraction for cross-origin tracking
      const data = await apiService.searchAdmissions(searchValue);
      setAdmissions(data || []);
      setRenewData({});
      setSearched(true);
    } catch (err) {
      alert("❌ " + err.message);
      setAdmissions([]);
      setSearched(true);
    }
  };

  const handleChange = (admissionId, field, value) => {
    setRenewData(prev => ({
      ...prev,
      [admissionId]: {
        ...prev[admissionId],
        [field]: value
      }
    }));
  };

  const renewAdmission = async (admissionId) => {
    const data = renewData[admissionId];
    if (!data?.endDate || !data?.feesPaid) {
      alert("Please fill all renewal fields");
      return;
    }
    const confirmRenew = window.confirm("Are you sure you want to renew this admission?");
    if (!confirmRenew) return;

    try {
      // Replaced raw PUT URL parameters string interpolation
      await apiService.renewAdmission(admissionId, data.endDate, Number(data.feesPaid));
      
      alert("✅ Admission Renewed Successfully");
      setAdmissions(prev => prev.filter(adm => adm.id !== admissionId));
    } catch (err) {
      alert("❌ " + err.message);
    }
  };

  const resetForm = (admissionId) => {
    setRenewData(prev => ({
      ...prev,
      [admissionId]: { endDate: "", feesPaid: "" }
    }));
  };

  return (
    <div className="ra-page">

      {/* ── Header card ── */}
      <div className="ra-header-card">
        <div className="ra-header-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.2">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
        </div>
        <div>
          <h2 className="ra-title">Renew Admission</h2>
          <p className="ra-subtitle">Search a student to renew their seat admission</p>
        </div>
      </div>

      {/* ── Search bar ── */}
      <div className="ra-search-row">
        <input
          className="ra-search-input"
          type="text"
          placeholder="Search by Name / Mobile / Seat / Registration No"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && searchAdmission()}
        />
        <button className="ra-search-btn" onClick={searchAdmission}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          Search
        </button>
      </div>

      {/* ── No results ── */}
      {searched && admissions.length === 0 && (
        <div className="ra-empty">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.5">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <p>No admissions found. Try a different search.</p>
        </div>
      )}

      {/* ── Results ── */}
      {admissions.map((admission) => {
        const data = renewData[admission.id] || { endDate: "", feesPaid: "" };
        const isDisabled = !data.endDate || !data.feesPaid;

        return (
          <div key={admission.id} className="ra-card">

            {/* Student info section */}
            <div className="ra-card-header">
              <div className="ra-avatar">
                {admission.student?.fullName ? admission.student.fullName.charAt(0).toUpperCase() : "?"}
              </div>
              <div>
                <div className="ra-student-name">{admission.student?.fullName}</div>
                <div className="ra-student-sub">{admission.student?.primaryContact}</div>
              </div>
              <div className="ra-seat-badge">{admission.seat?.seatCode}</div>
            </div>

            <div className="ra-divider"></div>

            {/* Details grid */}
            <div className="ra-details-grid">
              <div className="ra-detail-item">
                <span className="ra-detail-label">Expiry Date</span>
                <span className="ra-detail-value">
                  {admission.endDate ? new Date(admission.endDate).toLocaleDateString("en-GB") : "N/A"}
                </span>
              </div>
              <div className="ra-detail-item">
                <span className="ra-detail-label">Last Fees Paid</span>
                <span className="ra-detail-value">₹{admission.feesPaid}</span>
              </div>
            </div>

            <div className="ra-divider"></div>

            {/* Renewal fields */}
            <div className="ra-section-label">
              <span className="ra-section-dot"></span>
              Renewal Details
            </div>

            <div className="ra-renew-grid">
              <div className="ra-field">
                <label className="ra-label">New End Date <span className="ra-required">*</span></label>
                <input
                  className="ra-input"
                  type="date"
                  value={data.endDate}
                  onChange={(e) => handleChange(admission.id, "endDate", e.target.value)}
                />
              </div>
              <div className="ra-field">
                <label className="ra-label">Fees Paid (₹) <span className="ra-required">*</span></label>
                <input
                  className="ra-input"
                  type="number"
                  placeholder="0"
                  value={data.feesPaid}
                  onChange={(e) => handleChange(admission.id, "feesPaid", e.target.value)}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="ra-actions">
              <button
                className="ra-btn ra-btn-reset"
                onClick={() => resetForm(admission.id)}
              >
                Reset
              </button>
              <button
                className={`ra-btn ra-btn-renew ${isDisabled ? "ra-btn-disabled" : ""}`}
                disabled={isDisabled}
                onClick={() => renewAdmission(admission.id)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Renew Admission
              </button>
            </div>

          </div>
        );
      })}
    </div>
  );
}

export default RenewAdmission;